import React, { useEffect } from 'react';
import PolicyLayout from '../components/PolicyLayout';

const tocItems = [
  { href: '#intro', label: '1. Introduction' },
  { href: '#eligibility', label: '2. Eligibility' },
  { href: '#account', label: '3. Account & Security' },
  { href: '#services', label: '4. Description of Services' },
  { href: '#conduct', label: '5. User Conduct' },
  { href: '#fees', label: '6. Fees and Charges' },
  { href: '#transactions', label: '7. Transactions' },
  { href: '#suspension', label: '8. Suspension & Termination' },
  { href: '#ip', label: '9. Intellectual Property' },
  { href: '#liability', label: '10. Disclaimer & Liability' },
  { href: '#dispute', label: '11. Dispute Resolution' },
  { href: '#changes', label: '12. Changes to These Terms' },
  { href: '#contact', label: '13. Contact Us' },
];

export default function TermsPage() {
  useEffect(() => { document.title = 'Chattak | Terms & Conditions'; }, []);

  return (
    <PolicyLayout
      title="terms"
      badgeLabel="Legal Document"
      badgeIcon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
      heroTitle='Terms &amp; <span class="js-text-underline" data-underline="sm">Conditions</span>'
      heroSubtitle="These terms govern your use of Chattak's mobile application and services. By using Chattak, you agree to be bound by these terms and our Privacy Policy."
      tocItems={tocItems}
    >
      <div className="policy-section" id="intro">
        <span className="section-number">Section 01</span>
        <h2>Introduction and Acceptance of Terms</h2>
        <p>Welcome to Chattak Electronic Money Institution ("Chattak EMI," "we," "us," or "our"). These Terms and Conditions ("Terms") govern your use of our mobile application (the "App") and all related services we provide (collectively, the "Services").</p>
        <div className="policy-highlight">
          <p>By creating an account, downloading the App, or using our Services, <strong>you agree to be bound by these Terms and our Privacy Policy.</strong> If you do not agree to these Terms, you may not use our Services.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="eligibility">
        <span className="section-number">Section 02</span>
        <h2>Eligibility</h2>
        <p>To use our Services, you must meet the following requirements:</p>
        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>Eligibility Requirements</h3>
          <ul>
            <li>Be at least 16 years old.</li>
            <li>Successfully complete our identity verification process.</li>
            <li>Agree to comply with these Terms and all applicable Afghan laws and regulations.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="account">
        <span className="section-number">Section 03</span>
        <h2>Account Registration and Security</h2>
        <div className="content-img-placeholder">
          <img src="/images/account_registration.png" alt="Account and KYC illustration" />
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg></span>a) Account Creation</h3>
          <ul>
            <li>You must register for an account and provide accurate, current, and complete information as required.</li>
            <li>This includes providing personal identification documents such as the Afghan national electronic identity card (e-Tazkira) or Passport/visa if registering as a foreigner, as required by our KYC/CDD procedures.</li>
          </ul>
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>b) Your Responsibilities</h3>
          <ul>
            <li>Maintaining the confidentiality of your account password, PIN, and any other security credentials.</li>
            <li>All activities that occur under your account, whether or not you have authorized them.</li>
            <li>Ensuring that your account information is kept up to date.</li>
          </ul>
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>c) Unauthorized Use</h3>
          <ul>
            <li>You must notify us immediately if you suspect any unauthorized access to or use of your account.</li>
            <li>We are not liable for any loss you may incur as a result of someone else using your account, but you may be held liable for losses incurred by Chattak EMI or another party.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="services">
        <span className="section-number">Section 04</span>
        <h2>Description of Services</h2>
        <p>Chattak EMI is an Electronic Money Institution licensed by Da Afghanistan Bank (DAB). Our Services allow you to:</p>
        <div className="services-grid">
          <div className="service-card">
            <div className="service-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            </div>
            <div className="service-card-text">
              <h4>Hold, Send & Receive</h4>
              <p>Manage your electronic money wallet with ease.</p>
            </div>
          </div>
          <div className="service-card">
            <div className="service-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            </div>
            <div className="service-card-text">
              <h4>Merchant Payments</h4>
              <p>Make payments to merchants and other Chattak users.</p>
            </div>
          </div>
          <div className="service-card">
            <div className="service-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            </div>
            <div className="service-card-text">
              <h4>Transaction History</h4>
              <p>View your full transaction history and account balance.</p>
            </div>
          </div>
          <div className="service-card">
            <div className="service-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div className="service-card-text">
              <h4>Future Services</h4>
              <p>Other financial services that we may introduce from time to time.</p>
            </div>
          </div>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="conduct">
        <span className="section-number">Section 05</span>
        <h2>User Conduct and Prohibited Activities</h2>
        <p>You agree to use our Services only for lawful purposes and in accordance with these Terms. The following activities are strictly prohibited:</p>
        <ul className="prohibited-list">
          <li>Use our Services for any fraudulent, illegal, or malicious activities, including money laundering, terrorist financing, or other financial crimes.</li>
          <li>Violate any applicable laws or regulations of Afghanistan.</li>
          <li>Provide false, inaccurate, or misleading information during registration or at any time.</li>
          <li>Attempt to gain unauthorized access to our systems or another user's account.</li>
          <li>Reverse engineer, decompile, or disassemble any part of our App or Services.</li>
          <li>Engage in any activity that could disrupt, damage, or impair our Services.</li>
        </ul>
        <div className="policy-highlight" style={{ marginTop: '1.5rem' }}>
          <p>We actively monitor transactions for suspicious activity and are <strong>legally obligated to report such activity</strong> to the Financial Transactions and Reports Analysis Center of Afghanistan (FinTRACA).</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="fees">
        <span className="section-number">Section 06</span>
        <h2>Fees and Charges</h2>
        <p>You are responsible for paying any applicable fees for using our Services. A schedule of our current fees for transactions and other services will be made available to you within the App and on our website.</p>
        <div className="policy-warning">
          <p>We reserve the right to change our fees at any time. <strong>We will provide you with advance notice of any such changes</strong> before they take effect.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="transactions">
        <span className="section-number">Section 07</span>
        <h2>Transactions</h2>
        <p>All transactions initiated from your account are your responsibility. You must ensure that you have sufficient funds in your e-money wallet to cover the transaction amount and any applicable fees.</p>
        <div className="policy-highlight">
          <p><strong>Once you have confirmed a transaction, it cannot be reversed.</strong> We will make reasonable efforts to process your transactions in a timely manner but make no guarantees regarding processing times.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="suspension">
        <span className="section-number">Section 08</span>
        <h2>Suspension and Termination of Your Account</h2>
        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span>a) By Chattak EMI</h3>
          <ul>
            <li>We reserve the right to suspend or terminate your account at our sole discretion, without prior notice, if you breach these Terms.</li>
            <li>We are required to do so by law or a regulatory authority like DAB.</li>
            <li>We suspect your account is being used for fraudulent or illegal activities.</li>
            <li>Your account is inactive for an extended period.</li>
          </ul>
        </div>
        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>b) By You</h3>
          <ul>
            <li>You may close your account at any time by contacting our customer support team.</li>
            <li>Before closing your account, you must withdraw any remaining e-money balance.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="ip">
        <span className="section-number">Section 09</span>
        <h2>Intellectual Property</h2>
        <p>All content, trademarks, logos, and software within our App and Services are the exclusive property of Chattak EMI or its licensors.</p>
        <div className="policy-warning">
          <p>You are granted a <strong>limited, non-exclusive, non-transferable license</strong> to use the App for your personal, non-commercial use only. No other rights are granted.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="liability">
        <span className="section-number">Section 10</span>
        <h2>Disclaimer of Warranties and Limitation of Liability</h2>
        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>a) Disclaimer</h3>
          <ul>
            <li>Our Services are provided "as is" and "as available" without any warranties of any kind, either express or implied.</li>
            <li>We do not guarantee that our Services will be uninterrupted, error-free, or completely secure.</li>
          </ul>
        </div>
        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg></span>b) Limitation of Liability</h3>
          <ul>
            <li>To the fullest extent permitted by Afghan law, Chattak EMI shall not be liable for any indirect, incidental, special, or consequential damages.</li>
            <li>This includes but is not limited to loss of profits, data, or goodwill, arising from your use of or inability to use our Services.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="dispute">
        <span className="section-number">Section 11</span>
        <h2>Dispute Resolution</h2>
        <p>If you have a complaint or dispute, please contact our customer support team first. We are committed to resolving issues fairly and efficiently through our internal complaint resolution mechanism.</p>
        <div className="policy-highlight">
          <p>These Terms are governed by <strong>the laws of Afghanistan</strong>. Any legal action or proceeding arising out of these Terms shall be brought exclusively in the courts of Afghanistan.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="changes">
        <span className="section-number">Section 12</span>
        <h2>Changes to These Terms</h2>
        <p>We may amend these Terms from time to time. We will notify you of any changes by posting the updated Terms in the App and updating the "Last Updated" date.</p>
        <p>Your continued use of the Services after such changes constitutes your acceptance of the new Terms.</p>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="contact">
        <span className="section-number">Section 13</span>
        <h2>Contact Us</h2>
        <p>If you have any questions about these Terms and Conditions, please reach out to us:</p>
        <div className="contact-card">
          <div className="contact-card-info">
            <span className="contact-name">Chattak EMI</span>
            <span className="contact-detail">Traffic Square, PD4, Kabul, Afghanistan</span>
          </div>
          <div className="contact-card-info">
            <span className="contact-detail" style={{ opacity: .5, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Email</span>
            <a href="mailto:legal@chattak.com">legal@chattak.com</a>
          </div>
          <div className="contact-card-info">
            <span className="contact-detail" style={{ opacity: .5, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Phone</span>
            <a href="tel:+93787282840">+93 78 728 2840</a>
          </div>
        </div>
      </div>
    </PolicyLayout>
  );
}
